import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Building2, FolderKanban, Loader2, Package, Pin, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { TagBadge } from "@/components/ui/TagBadge";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useMeetingList } from "@/modules/meetings/hooks/useMeetings";
import { useTaskList } from "@/modules/tasks/hooks/useTasks";
import { useActionList } from "@/modules/actions/hooks/useActions";
import {
  useCreateNote,
  useNoteStatuses,
  useNoteTags,
  useNoteTypes,
} from "../hooks/useNotes";

type LockedFields = {
  companyId?: boolean;
  productId?: boolean;
  projectId?: boolean;
  meetingId?: boolean;
  taskId?: boolean;
  actionId?: boolean;
};

export type NotePrefill = {
  companyId?: string | null;
  productId?: string | null;
  projectId?: string | null;
  meetingId?: string | null;
  meetingTitle?: string | null;
  taskId?: string | null;
  taskTitle?: string | null;
  taskCode?: string | null;
  actionId?: string | null;
  actionTitle?: string | null;
  actionCode?: string | null;
  locked?: LockedFields;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (code: string) => void;
  prefill?: NotePrefill;
};

export function NewNoteDrawer({ isOpen, onClose, onCreated, prefill }: Props) {
  const createNote = useCreateNote();
  const navigate = useNavigate();
  const noteTypes = useNoteTypes().data ?? [];
  const noteStatuses = useNoteStatuses().data ?? [];
  const noteTagOptions = useNoteTags().data ?? [];

  const [type, setType] = useState("general");
  const [status, setStatus] = useState("draft");
  const [visibility, setVisibility] = useState<"team" | "private">("team");
  const [tags, setTags] = useState<string[]>([]);

  const lockAction = !!prefill?.locked?.actionId && !!prefill?.actionId;
  const lockTask = !!prefill?.locked?.taskId && !!prefill?.taskId;
  const lockMeeting = !!prefill?.locked?.meetingId && !!prefill?.meetingId;
  const lockProject = !!prefill?.locked?.projectId && !!prefill?.projectId;
  const lockCompany = !!prefill?.locked?.companyId && !!prefill?.companyId;
  const lockProduct = !!prefill?.locked?.productId && !!prefill?.productId;

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [isPinned, setIsPinned] = useState(false);
  const [companyId, setCompanyId] = useState(prefill?.companyId ?? "");
  const [productId, setProductId] = useState(prefill?.productId ?? "");
  const [projectId, setProjectId] = useState(prefill?.projectId ?? "");
  const [meetingId, setMeetingId] = useState(prefill?.meetingId ?? "");
  const [taskId, setTaskId] = useState(prefill?.taskId ?? "");
  const [actionId, setActionId] = useState(prefill?.actionId ?? "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setTitle("");
      setContent("");
      setIsPinned(false);
      setCompanyId(prefill?.companyId ?? "");
      setProductId(prefill?.productId ?? "");
      setProjectId(prefill?.projectId ?? "");
      setMeetingId(prefill?.meetingId ?? "");
      setTaskId(prefill?.taskId ?? "");
      setActionId(prefill?.actionId ?? "");
      setType("general");
      setStatus("draft");
      setVisibility("team");
      setTags([]);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const { data: companiesResult } = useCompanyList({
    page: 1,
    limit: 100,
    operationalOnly: true,
  });
  const companies = companiesResult?.data ?? [];

  const { data: productsResult } = useProductList({
    page: 1,
    limit: 100,
    companyId: companyId || undefined,
  });
  const products = productsResult?.data ?? [];

  const { data: projectsResult } = useProjectList({
    page: 1,
    limit: 100,
    companyId: companyId || undefined,
    productId: productId || undefined,
  });
  const projects = projectsResult?.data ?? [];

  const { data: meetingsResult } = useMeetingList({
    page: 1,
    limit: 100,
    projectId: projectId || undefined,
  });
  const meetings = meetingsResult?.data ?? [];

  const { data: tasksResult } = useTaskList({
    page: 1,
    limit: 100,
    projectId: projectId || undefined,
  });
  const tasks = tasksResult?.data ?? [];

  const { data: actionsResult } = useActionList({
    page: 1,
    limit: 100,
    projectId: projectId || undefined,
  });
  const actions = actionsResult?.data ?? [];

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    setError(null);
    try {
      const res = await createNote.mutateAsync({
        title: title.trim(),
        content,
        type,
        status,
        tags,
        visibility,
        isPinned,
        companyId: companyId || null,
        productId: productId || null,
        projectId: projectId || null,
        meetingId: meetingId || null,
        taskId: taskId || null,
        actionId: actionId || null,
      });
      onCreated?.(res.note.code ?? res.note.id);
      onClose();
      navigate(`/notes/${res.note.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create note");
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998] bg-black/30"
        onClick={onClose}
      />
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[600px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <h2 className="text-lg font-semibold text-foreground">New Note</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col overflow-y-auto"
        >
          <div className="flex-1 space-y-5 p-6">
            <div className="space-y-2">
              <Label htmlFor="note-title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="note-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter note title"
                required
              />
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setIsPinned((v) => !v)}
                className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors"
                style={{
                  borderColor: isPinned
                    ? "hsl(40,95%,60%)"
                    : "hsl(210,30%,85%)",
                  background: isPinned ? "hsl(40,95%,95%)" : "white",
                  color: isPinned ? "hsl(40,95%,30%)" : "hsl(215,16%,47%)",
                }}
              >
                <Pin
                  className="h-[14px] w-[14px]"
                  strokeWidth={1.5}
                  style={{
                    fill: isPinned ? "hsl(40,95%,55%)" : "none",
                  }}
                />
                {isPinned ? "Pinned" : "Pin this note"}
              </button>
            </div>

            <div className="space-y-3 rounded-lg border p-4" style={{ borderColor: "hsl(210,30%,90%)" }}>
              <div className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
                Association
              </div>

              <div className="space-y-2">
                <Label htmlFor="note-company">Company</Label>
                <div className="relative">
                  <Building2
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    strokeWidth={1.5}
                  />
                  <select
                    id="note-company"
                    value={companyId}
                    disabled={lockCompany || lockAction || lockTask || lockMeeting || lockProject}
                    onChange={(e) => {
                      setCompanyId(e.target.value);
                      setProductId("");
                      setProjectId("");
                    }}
                    className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  >
                    <option value="">No company</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="note-product">Product</Label>
                <div className="relative">
                  <Package
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    strokeWidth={1.5}
                  />
                  <select
                    id="note-product"
                    value={productId}
                    disabled={lockProduct || lockAction || lockTask || lockMeeting || lockProject}
                    onChange={(e) => {
                      setProductId(e.target.value);
                      setProjectId("");
                    }}
                    className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  >
                    <option value="">No product</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="note-project">Project</Label>
                <div className="relative">
                  <FolderKanban
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    strokeWidth={1.5}
                  />
                  <select
                    id="note-project"
                    value={projectId}
                    disabled={lockProject || lockAction || lockTask || lockMeeting}
                    onChange={(e) => setProjectId(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  >
                    <option value="">No project</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {lockMeeting && prefill?.meetingTitle ? (
                <div className="space-y-2">
                  <Label>Meeting</Label>
                  <div
                    className="rounded-md border px-3 py-2 text-sm text-muted-foreground"
                    style={{
                      borderColor: "hsl(210,30%,85%)",
                      background: "hsl(210,16%,97%)",
                    }}
                  >
                    {prefill.meetingTitle}
                  </div>
                </div>
              ) : !lockTask && !lockAction ? (
                <div className="space-y-2">
                  <Label htmlFor="note-meeting">Meeting</Label>
                  <select
                    id="note-meeting"
                    value={meetingId}
                    onChange={(e) => setMeetingId(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  >
                    <option value="">No meeting</option>
                    {meetings.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.title}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {lockTask && (prefill?.taskCode || prefill?.taskTitle) ? (
                <div className="space-y-2">
                  <Label>Task</Label>
                  <div
                    className="rounded-md border px-3 py-2 text-sm text-muted-foreground"
                    style={{
                      borderColor: "hsl(210,30%,85%)",
                      background: "hsl(210,16%,97%)",
                    }}
                  >
                    <span className="font-mono">{prefill.taskCode}</span>
                    {prefill.taskTitle ? ` — ${prefill.taskTitle}` : ""}
                  </div>
                </div>
              ) : !lockMeeting && !lockAction ? (
                <div className="space-y-2">
                  <Label htmlFor="note-task">Task</Label>
                  <select
                    id="note-task"
                    value={taskId}
                    onChange={(e) => setTaskId(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  >
                    <option value="">No task</option>
                    {tasks.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.code} — {t.title}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {lockAction && (prefill?.actionCode || prefill?.actionTitle) ? (
                <div className="space-y-2">
                  <Label>Action</Label>
                  <div
                    className="rounded-md border px-3 py-2 text-sm text-muted-foreground"
                    style={{
                      borderColor: "hsl(210,30%,85%)",
                      background: "hsl(210,16%,97%)",
                    }}
                  >
                    <span className="font-mono">{prefill.actionCode}</span>
                    {prefill.actionTitle ? ` — ${prefill.actionTitle}` : ""}
                  </div>
                </div>
              ) : !lockMeeting && !lockTask ? (
                <div className="space-y-2">
                  <Label htmlFor="note-action">Action</Label>
                  <select
                    id="note-action"
                    value={actionId}
                    onChange={(e) => setActionId(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  >
                    <option value="">No action</option>
                    {actions.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} — {a.title}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="note-type">Type</Label>
                <select
                  id="note-type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {noteTypes.length === 0 ? (
                    <option value="general">General</option>
                  ) : (
                    noteTypes.map((t) => (
                      <option key={t.id} value={t.value}>
                        {t.label}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="note-status">Status</Label>
                <select
                  id="note-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {noteStatuses.length === 0 ? (
                    <>
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </>
                  ) : (
                    noteStatuses.map((s) => (
                      <option key={s.id} value={s.value}>
                        {s.label}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Visibility</Label>
              <div className="inline-flex rounded-md border p-0.5" style={{ borderColor: "hsl(210,30%,85%)" }}>
                {(["team", "private"] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setVisibility(v)}
                    className="px-3 py-1 text-xs font-medium rounded transition-colors"
                    style={{
                      background: visibility === v ? "hsl(210,55%,88%)" : "transparent",
                      color: visibility === v ? "hsl(210,100%,25%)" : "hsl(215,16%,47%)",
                    }}
                  >
                    {v === "team" ? "Team" : "Private"}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-1.5">
                {noteTagOptions.map((tagOpt) => {
                  const selected = tags.includes(tagOpt.value);
                  return (
                    <button
                      key={tagOpt.id}
                      type="button"
                      onClick={() =>
                        setTags((prev) =>
                          prev.includes(tagOpt.value)
                            ? prev.filter((x) => x !== tagOpt.value)
                            : [...prev, tagOpt.value],
                        )
                      }
                      style={{
                        opacity: selected ? 1 : 0.55,
                        filter: selected ? "none" : "grayscale(0.3)",
                      }}
                    >
                      <TagBadge value={tagOpt.value} label={tagOpt.label} size="md" />
                    </button>
                  );
                })}
                {noteTagOptions.length === 0 && (
                  <span className="text-xs text-muted-foreground">
                    No tags configured yet. Add them in Dropdown Manager.
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Content</Label>
              <RichTextEditor
                content={content}
                onChange={setContent}
                toolbarVariant="full"
                minHeight={250}
                placeholder="Write your note..."
              />
            </div>

            {error && (
              <div
                className="rounded-md border px-3 py-2 text-sm"
                style={{
                  borderColor: "hsl(0,70%,80%)",
                  background: "hsl(0,70%,96%)",
                  color: "hsl(0,70%,35%)",
                }}
              >
                {error}
              </div>
            )}
          </div>

          <div
            className="flex items-center justify-end gap-3 border-t px-6 py-4"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!title.trim() || createNote.isPending}
            >
              {createNote.isPending && (
                <Loader2
                  className="mr-1 h-4 w-4 animate-spin"
                  strokeWidth={1.5}
                />
              )}
              Create Note
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
